export const APP_PAGES = {
  SWAPS:       "swaps",
  NEW_SWAP:    "new_swap",
  FIND_SWAP:   "find_swap",
  API_TOKENS:  "api_tokens",
  SETTINGS:    "settings"
} as const;

// A public_token is urlsafe_base64(24) with - and _ mapped to a/b, so it is
// exactly 32 URL-safe characters. Used to validate a pasted lookup code.
export const PUBLIC_TOKEN_RE = /^[A-Za-z0-9]{32}$/;

// Accepts a bare code or a full /o/<code> URL pasted from the address bar.
export const extractPublicToken = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const fromUrl = trimmed.match(/\/o\/([A-Za-z0-9]+)/);
  const candidate = fromUrl ? fromUrl[1] : trimmed;
  return PUBLIC_TOKEN_RE.test(candidate) ? candidate : null;
};

export type AppPageType = typeof APP_PAGES[keyof typeof APP_PAGES];

export const BOOTSTRAP_SCRIPT_ID = "__app_bootstrap";

export const PAY_IN_ASSETS = [
  "SOL_SOL",
  "USDT_TRON",
  "USDT_SOL",
  "USDC_SOL",
  "ETH_ETH",
  "USDT_ETH",
  "USDC_ETH"
] as const;

export type PayInAssetCode = typeof PAY_IN_ASSETS[number];

export const ASSET_NAMES: Record<string, string> = {
  SOL_SOL:   "Solana",
  USDT_TRON: "Tether on Tron",
  USDT_SOL:  "Tether on Solana",
  USDC_SOL:  "USDC on Solana",
  ETH_ETH:   "Ethereum",
  USDT_ETH:  "Tether on Ethereum",
  USDC_ETH:  "USDC on Ethereum",
  BTCLN:     "Bitcoin Lightning"
};

export const assetName = (code: string): string => ASSET_NAMES[code] ?? code;

/** Emphatic deposit copy: currency + network (wrong pair = lost funds). */
export const ASSET_DEPOSIT_LABELS: Record<string, string> = {
  SOL_SOL:   "Solana (SOL) on the Solana network",
  USDT_TRON: "Tether USD (USDT) on the Tron network",
  USDT_SOL:  "Tether USD (USDT) on the Solana network",
  USDC_SOL:  "USDC on the Solana network",
  ETH_ETH:   "Ethereum (ETH) on the Ethereum network",
  USDT_ETH:  "Tether USD (USDT) on the Ethereum network",
  USDC_ETH:  "USDC on the Ethereum network"
};

export const assetDepositLabel = (code: string): string =>
  ASSET_DEPOSIT_LABELS[code] ?? assetName(code);

export const PHASE_LABELS: Record<string, string> = {
  awaiting_deposit: "Awaiting deposit",
  confirming:       "Confirming",
  exchanging:       "Exchanging",
  paying_invoice:   "Paying invoice",
  completed:        "Completed",
  expired:          "Expired",
  refund_required:  "Refund required",
  refund_pending:   "Refund pending",
  refunded:         "Refunded",
  failed:           "Failed",
  attention:        "Needs attention"
};

/** Badge tone keys used by the React UI (`Badge` / `badge-*` classes). */
export const PHASE_COLORS: Record<string, "info" | "warn" | "success" | "danger" | "muted" | "accent"> = {
  awaiting_deposit: "info",
  confirming:       "warn",
  exchanging:       "warn",
  paying_invoice:   "accent",
  completed:        "success",
  expired:          "muted",
  refund_required:  "danger",
  refund_pending:   "danger",
  refunded:         "info",
  failed:           "danger",
  attention:        "danger"
};

// Server-side reasons (ApiRefunds / ProviderEvents / LightningPayouts) rendered
// as something a payer can act on. Raw reason slugs used to leak into the UI.
export const ATTENTION_MESSAGES: Record<string, string> = {
  refund_uneconomic:
    "The amount received is too small to cover the network fee for a return. Contact support and we will settle it manually.",
  refund_attempts_exhausted:
    "We could not broadcast the refund automatically. Support has been notified and will complete it manually.",
  refund_reply_ambiguous:
    "Your refund was submitted but we are still confirming it on-chain. Do not resubmit — this resolves automatically.",
  payout_settled_during_refund:
    "The Lightning payment and a refund overlapped on this order. Support is reconciling it now.",
  refund_requested_with_payout_in_flight:
    "A refund was requested after this order's Lightning payout had already started. Support is reviewing it.",
  deposit_after_payout:
    "An extra deposit arrived after this order's Lightning payout had already started or settled. Support will review it — the original swap amount is not refundable.",
  duplicate_refund_broadcast:
    "More than one refund transaction was detected for this order. Support is reviewing it.",
  repeat_deposit:
    "We received more than one deposit for this order. Support will return the extra amount.",
  provider_reported_emergency:
    "The amount received did not match the order. Support will help resolve it.",
  overpaid:
    "You sent more than the ordered amount. Lightning payout cannot increase — request a full refund of the deposited amount.",
  post_terminal_deposit:
    "A deposit arrived after this order had already finished. Support will return it."
};

export const attentionMessage = (reason?: string | null): string => {
  if (!reason) return "This order needs manual review. Please contact support.";
  const exact = ATTENTION_MESSAGES[reason];
  if (exact) return exact;
  // Reasons like "refund_broadcast_in_completed" or "invalid_payout_invoice:…"
  const prefix = Object.keys(ATTENTION_MESSAGES).find((k) => reason.startsWith(k));
  if (prefix) return ATTENTION_MESSAGES[prefix];
  return "This order needs manual review. Please contact support with your order ID.";
};

// FixedFloat emergency.status codes (LESS/MORE/EXPIRED/LIMIT) plus refund_reason
// slugs — turn them into copy a payer can act on. Never surface the raw codes.
export const emergencyExplanation = (
  statuses: string[],
  reason?: string | null,
  opts?: { receivedAmount?: string | null; assetLabel?: string; refundLapsed?: boolean }
): { title: string; body: string } => {
  const set = new Set(statuses.map((s) => s.toUpperCase()));
  const outsideLimits = set.has("LIMIT");
  const refundCta = opts?.refundLapsed
    ? "No refund was requested within 7 days, so this deposit is no longer refundable."
    : "Submit a refund address below to get your deposit back.";
  const received =
    opts?.receivedAmount && opts.assetLabel
      ? `${opts.receivedAmount} ${opts.assetLabel}`
      : opts?.receivedAmount || null;

  if (reason === "repeat_deposit") {
    return {
      title: "Extra deposit received",
      body:
        "We received more than one deposit for this order. Lightning payout cannot proceed — " +
        refundCta
    };
  }

  if (reason === "late_deposit" || set.has("EXPIRED")) {
    return {
      title: "Deposit arrived too late",
      body:
        "Your deposit confirmed after this swap expired, so we cannot pay the Lightning invoice. " +
        refundCta
    };
  }

  if (reason === "overpaid" || set.has("MORE")) {
    return {
      title: received ? `You sent too much: ${received}` : "You sent too much",
      body: outsideLimits
        ? "The amount we received is above the maximum for this pair. Lightning payout cannot increase — " +
          refundCta
        : "You sent more than the ordered amount. Lightning payout cannot increase — " + refundCta
    };
  }

  if (reason === "underpaid" || set.has("LESS")) {
    return {
      title: received ? `You sent too little: ${received}` : "You sent too little",
      body: outsideLimits
        ? "The amount we received is below the minimum for this pair, so we cannot pay your Lightning invoice. " +
          refundCta
        : "The amount we received is less than required for this swap, so we cannot pay your Lightning invoice. " +
          refundCta
    };
  }

  return {
    title: "Deposit cannot be swapped",
    body: "We cannot complete this swap with the deposit received. " + refundCta
  };
};

// Phases where a deposit address must NOT be advertised any more — showing the
// QR next to a refund form invites a second payment we would have to return.
export const DEPOSIT_CLOSED_PHASES = [
  "refund_required",
  "refund_pending",
  "refunded",
  "attention",
  "completed",
  "expired",
  "failed"
];

export const satsToBtc = (sats: number): string => {
  return (sats / 1e8).toFixed(8);
};

/** Approximate deposit for an invoice against a rate row (SPA gating only). */
export const depositForInvoice = (sats: number, rate: { in: string; out: string; tofee: string }): number => {
  const invoiceBtc = sats / 1e8;
  const tofee = Number(rate.tofee);
  const inn = Number(rate.in);
  const out = Number(rate.out);
  if (!(out > 0) || !(inn > 0)) return Number.POSITIVE_INFINITY;
  return (invoiceBtc + tofee) * (inn / out);
};

export const invoiceFitsRate = (
  sats: number,
  rate: { online: boolean; in: string; out: string; tofee: string; min_from: string; max_from: string }
): boolean => {
  if (!rate.online) return false;
  const from = depositForInvoice(sats, rate);
  return from >= Number(rate.min_from) && from <= Number(rate.max_from);
};

/** Smallest whole-sat invoice that clears this pair's min_from (ceil). */
export const minInvoiceSatsForRate = (
  rate: { in: string; out: string; tofee: string; min_from: string }
): number => {
  const tofee = Number(rate.tofee);
  const inn = Number(rate.in);
  const out = Number(rate.out);
  const minFrom = Number(rate.min_from);
  if (!(inn > 0) || !(out > 0)) return Number.POSITIVE_INFINITY;
  const btc = minFrom * (out / inn) - tofee;
  return Math.max(1, Math.ceil(btc * 1e8));
};

export const formatQuoteError = (code: string): string => {
  switch (code) {
    case "LIMIT_MIN":
      return "Invoice is below the minimum for this asset.";
    case "LIMIT_MAX":
      return "Invoice is above the maximum for this asset.";
    case "OFFLINE_FROM":
      return "This pair is temporarily offline.";
    default:
      return code;
  }
};

export const formatTimeLeft = (seconds: number): string => {
  if (seconds <= 0) return "Expired";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  }
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export const fromQueryString = (key: string): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
};

export const setQueryString = (key: string, value: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set(key, value);
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, "", newUrl);
};

export const removeQueryString = (...keys: string[]) => {
  const params = new URLSearchParams(window.location.search);
  keys.forEach((k) => params.delete(k));
  const qs = params.toString();
  const newUrl = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;
  window.history.replaceState({}, "", newUrl);
};

// "?invoice=lnbc..." — the shareable pay link. Anything that is not shaped like
// a BOLT11 invoice is ignored rather than pasted into the form for the server
// to reject; the real amount/expiry check still happens on parse.
export const invoiceFromQueryString = (): string | null => {
  const raw = fromQueryString("invoice");
  if (!raw) return null;
  const invoice = raw.trim().toLowerCase();
  return /^ln[a-z0-9]{20,1990}$/.test(invoice) ? invoice : null;
};

export const payInvoiceUrl = (bolt11: string): string =>
  `${window.location.origin}/?invoice=${encodeURIComponent(bolt11.trim())}`;

// /o/<public_token> — the bookmarkable swap page. Returns null on every other
// path, including the "/" landing page.
export const publicTokenFromPath = (): string | null => {
  const match = window.location.pathname.match(/^\/o\/([A-Za-z0-9]+)\/?$/);
  return match ? match[1] : null;
};

export const publicOrderUrl = (publicToken: string): string =>
  `${window.location.origin}/o/${publicToken}`;

export const coinFromAssetCode = (code: string): string => {
  const parts = code.split("_");
  return parts[0]?.toLowerCase() ?? "generic";
};
